import { setAdoc_t } from "../redux/reducer/userSlice";
import { getUserInfo, resetUserInfoThrottle } from "../redux/actions/userActions";
import { fetchAppPermissions } from "../redux/actions/permissionsActions";
import { clearRegistrationTokens, saveCurrentEnvironment } from "./authGuard";
import { endImpersonatedSession, writeAuth } from "./authStorage";
import { identifyFunnelUser } from "./funnelTracker";
import {
  hydrateGoalsCatalogFromApi,
  hydrateInitialConfigFromProfile,
  isOnboardingIncomplete,
  onboardingResumePath,
} from "./onboardingProfile";

export const LOGIN_PATH = "/login";
// The same screen, leading with the device's social button (client, 14/09/2026).
export const REGISTRATION_PATH = "/login?modo=registrazione";

export const isRegistrationSearch = (search) =>
  new URLSearchParams(search || "").get("modo") === "registrazione";

/**
 * Store a bearer token and load the member's profile: shared by Google/Apple,
 * the email code and the email button.
 *
 * @returns {Promise<{profile: object, incomplete: boolean, nextPath: string}>}
 */
export const completeSignIn = async ({ token, dispatch }) => {
  dispatch(setAdoc_t(token));
  // A support session may have left a per-tab session behind; drop it first so
  // a real sign-in lands in the shared store and persists across visits.
  endImpersonatedSession();
  writeAuth("adoc_t", token);
  saveCurrentEnvironment();
  clearRegistrationTokens();

  // Stitch this device to the account. Fire-and-forget: it flushes the queued
  // anonymous events first, and it must never delay the redirect into the app.
  identifyFunnelUser();

  // Fetch profile + goals catalog in parallel (goals are small & harmless to prefetch)
  resetUserInfoThrottle(); // fresh login — force fetch
  const [profileResult] = await Promise.allSettled([
    dispatch(getUserInfo())
      .unwrap()
      .catch((err) => {
        if (err?.name === "ConditionError") return; // throttled — data is fresh
        throw err;
      }),
    dispatch(fetchAppPermissions())
      .unwrap()
      .catch(() => {}),
    hydrateGoalsCatalogFromApi(dispatch),
  ]);

  if (profileResult.status === "rejected") {
    throw new Error("Profilo non disponibile.");
  }

  const profile = profileResult.value;
  const incomplete = isOnboardingIncomplete(profile);

  if (incomplete) {
    hydrateInitialConfigFromProfile(dispatch, profile);
  }

  return {
    profile,
    incomplete,
    nextPath: incomplete ? onboardingResumePath(profile) : "/home",
  };
};
